import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import api from '../services/api';
import { 
  Users, UserPlus, Shield, ShieldOff, Ban, CheckCircle, 
  Search, X, Edit2, Trash2, ChevronDown, ChevronUp,
  MessageSquare, Crown, User as UserIcon, Mail, MailCheck, Key, RefreshCw,
  Activity, Lock, FileText, Stethoscope, Clock, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const FEATURES = [
  { key: 'mood', label: 'Mood Journal' },
  { key: 'medications', label: 'Medications' },
  { key: 'appointments', label: 'Appointments' },
  { key: 'talk', label: 'Just Talk' },
  { key: 'store', label: 'Wellness Store' },
  { key: 'emergency', label: 'Emergency' },
  { key: 'ai_companion', label: 'AI Companion' },
  { key: 'mindpulse', label: 'MindPulse™', icon: Activity },
  { key: 'private_vault', label: 'Private Vault', icon: Lock },
  { key: 'doctor_summary', label: 'Doctor Summary', icon: FileText },
];

const ROLES = [
  { value: 'user', label: 'Normal User', icon: UserIcon, color: 'text-muted-foreground' },
  { value: 'superuser', label: 'Super User', icon: Shield, color: 'text-blue-400' },
  { value: 'admin', label: 'Admin', icon: Crown, color: 'text-yellow-400' },
];

const CreateUserModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    age: 18,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.createUser(formData);
      toast.success('User created successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Create New User
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              required
              data-testid="create-user-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              required
              minLength={6}
              data-testid="create-user-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Full Name</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
              required
              data-testid="create-user-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
                data-testid="create-user-role"
              >
                {ROLES.map((role) => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Age</label>
              <input
                type="number"
                min={13}
                max={120}
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground"
                data-testid="create-user-age"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-full bg-muted text-foreground font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-full bg-primary text-white font-semibold disabled:opacity-50"
              data-testid="create-user-submit"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserRow = ({ user, onUpdate, onDelete, onViewChats }) => {
  const [expanded, setExpanded] = useState(false);
  const [permissions, setPermissions] = useState(user.permissions || {});
  const [updating, setUpdating] = useState(false);

  const roleInfo = ROLES.find(r => r.value === user.role) || ROLES[0];
  const RoleIcon = roleInfo.icon;

  const handleRoleChange = async (newRole) => {
    setUpdating(true);
    try {
      await adminAPI.updateUser(user.user_id, { role: newRole });
      toast.success('Role updated successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setUpdating(false);
    }
  };

  const handleBanToggle = async () => {
    setUpdating(true);
    try {
      await adminAPI.updateUser(user.user_id, { banned: !user.banned });
      toast.success(user.banned ? 'User unbanned' : 'User banned');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update ban status');
    } finally {
      setUpdating(false);
    }
  };

  const handleVerifyEmail = async () => {
    setUpdating(true);
    try {
      await adminAPI.updateUser(user.user_id, { email_verified: true });
      toast.success('User email verified successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to verify email');
    } finally {
      setUpdating(false);
    }
  };

  const handlePermissionToggle = async (feature) => {
    const newValue = !permissions[feature];
    setUpdating(true);
    try {
      await adminAPI.updateUserPermission(user.user_id, feature, newValue);
      setPermissions({ ...permissions, [feature]: newValue });
      toast.success(`${feature} ${newValue ? 'granted' : 'revoked'}`);
    } catch (error) {
      toast.error('Failed to update permission');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetVaultPin = async () => {
    if (!window.confirm(`Reset vault PIN for ${user.email}? A temporary PIN will be sent to their email. Their journal entries will be preserved.`)) {
      return;
    }
    
    setUpdating(true);
    try {
      const response = await api.post(`/admin/reset-vault-pin/${user.user_id}`);
      toast.success(response.data.message || 'Vault PIN reset successfully');
      if (!response.data.email_sent) {
        toast.warning('Email could not be sent. Check SMTP configuration.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset vault PIN';
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset password for ${user.email}? A temporary password will be generated and sent to their email.`)) {
      return;
    }
    
    setUpdating(true);
    try {
      const response = await api.post(`/admin/users/${user.user_id}/reset-password`);
      if (response.data.temp_password) {
        toast.success(`Password reset! Temp password: ${response.data.temp_password}`);
      } else {
        toast.success(response.data.message || 'Password reset successfully');
      }
      if (!response.data.email_sent) {
        toast.warning('Email notification could not be sent.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mb-3" data-testid="user-row">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full bg-muted flex items-center justify-center ${user.banned ? 'opacity-50' : ''}`}>
            <RoleIcon className={`w-5 h-5 ${roleInfo.color}`} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">{user.full_name || 'Unknown'}</span>
              {user.banned && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-destructive/20 text-destructive">Banned</span>
              )}
              {!user.email_verified && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">Unverified</span>
              )}
              {user.email_verified && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">Verified</span>
              )}
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                user.subscription_status === 'active' ? 'bg-blue-500/20 text-blue-400' : 'bg-muted text-muted-foreground'
              }`}>
                {user.subscription_status || 'inactive'}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">{user.email}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onViewChats(user); }}
            className="p-2 hover:bg-muted rounded-lg"
            title="View Chats"
            data-testid="view-chats-btn"
          >
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(user); }}
            className="p-2 hover:bg-destructive/20 rounded-lg"
            title="Delete User"
            data-testid="delete-user-btn"
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </button>
          {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-4 space-y-4">
          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">User Role</label>
            <div className="flex gap-2">
              {ROLES.map((role) => (
                <button
                  key={role.value}
                  onClick={() => handleRoleChange(role.value)}
                  disabled={updating || user.role === role.value}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                    user.role === role.value 
                      ? 'border-primary bg-primary/20 text-primary' 
                      : 'border-border hover:border-primary/50'
                  } disabled:opacity-50`}
                  data-testid={`role-${role.value}`}
                >
                  <role.icon className={`w-4 h-4 ${role.color}`} />
                  <span className="text-sm">{role.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Ban Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Account Status</span>
            <button
              onClick={handleBanToggle}
              disabled={updating}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                user.banned 
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                  : 'bg-destructive/20 text-destructive hover:bg-destructive/30'
              } disabled:opacity-50`}
              data-testid="ban-toggle"
            >
              {user.banned ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
              <span>{user.banned ? 'Unban User' : 'Ban User'}</span>
            </button>
          </div>

          {/* Email Verification - Only show for unverified users */}
          {!user.email_verified && (
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-foreground">Email Verification</span>
                <p className="text-xs text-muted-foreground">User has not verified their email</p>
              </div>
              <button
                onClick={handleVerifyEmail}
                disabled={updating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 disabled:opacity-50"
                data-testid="verify-email-btn"
              >
                <MailCheck className="w-4 h-4" />
                <span>Verify Email</span>
              </button>
            </div>
          )}

          {/* Reset Vault PIN */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-foreground">Private Vault PIN</span>
              <p className="text-xs text-muted-foreground">Reset user's vault PIN without deleting their journal</p>
            </div>
            <button
              onClick={handleResetVaultPin}
              disabled={updating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 disabled:opacity-50"
              data-testid="reset-vault-pin-btn"
            >
              {updating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              <span>Reset Vault PIN</span>
            </button>
          </div>

          {/* Reset Login Password */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-foreground">Login Password</span>
              <p className="text-xs text-muted-foreground">Generate a temporary password for this user</p>
            </div>
            <button
              onClick={handleResetPassword}
              disabled={updating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 disabled:opacity-50"
              data-testid="reset-password-btn"
            >
              {updating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              <span>Reset Password</span>
            </button>
          </div>

          {/* Feature Permissions */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Feature Permissions</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FEATURES.map((feature) => (
                <button
                  key={feature.key}
                  onClick={() => handlePermissionToggle(feature.key)}
                  disabled={updating}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                    permissions[feature.key] 
                      ? 'border-green-500/50 bg-green-500/10' 
                      : 'border-destructive/50 bg-destructive/10'
                  } disabled:opacity-50`}
                  data-testid={`permission-${feature.key}`}
                >
                  <span className="text-sm text-foreground">{feature.label}</span>
                  {permissions[feature.key] ? (
                    <Shield className="w-4 h-4 text-green-400" />
                  ) : (
                    <ShieldOff className="w-4 h-4 text-destructive" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* User Details */}
          <div className="text-xs text-muted-foreground pt-2 border-t border-border">
            <p>User ID: {user.user_id}</p>
            <p>Created: {new Date(user.created_at).toLocaleString()}</p>
            <p className={user.email_verified ? 'text-green-400' : 'text-yellow-400'}>
              Email: {user.email_verified ? 'Verified ✓' : 'Not Verified'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'doctors'
  const [approvingDoctor, setApprovingDoctor] = useState(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchPendingDoctors();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers(searchQuery);
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingDoctors = async () => {
    try {
      const response = await api.get('/admin/doctors/pending');
      setPendingDoctors(response.data.doctors || []);
    } catch (error) {
      console.error('Failed to load pending doctors:', error);
    }
  };

  const handleDoctorApproval = async (doctorId, approve) => {
    setApprovingDoctor(doctorId);
    try {
      if (approve) {
        await api.post(`/admin/doctors/${doctorId}/approve`);
        toast.success('Doctor approved successfully!');
      } else {
        await api.post(`/admin/doctors/${doctorId}/reject`);
        toast.success('Doctor rejected');
      }
      fetchPendingDoctors();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setApprovingDoctor(null);
    }
  };

  // Only admin can access
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!window.confirm(`Are you sure you want to delete ${userToDelete.email}? This action cannot be undone.`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(userToDelete.user_id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleViewChats = (userToView) => {
    window.open(`/admin/chats?user=${userToView.user_id}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background py-8" data-testid="user-management-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-3">
            <Users className="w-10 h-10 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
                User Management
              </h1>
              <p className="text-muted-foreground">Manage users, roles, and permissions</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-5 h-5" />
            Create User
          </button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-card border border-border text-foreground"
                data-testid="user-search-input"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-xl bg-muted text-foreground hover:bg-muted/80"
            >
              Search
            </button>
          </div>
        </form>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-foreground">{users.length}</p>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-yellow-400">{users.filter(u => u.role === 'admin').length}</p>
            <p className="text-sm text-muted-foreground">Admins</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-blue-400">{users.filter(u => u.role === 'superuser').length}</p>
            <p className="text-sm text-muted-foreground">Super Users</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-cyan-400">{users.filter(u => u.user_type === 'doctor').length}</p>
            <p className="text-sm text-muted-foreground">Doctors</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-2xl font-bold text-destructive">{users.filter(u => u.banned).length}</p>
            <p className="text-sm text-muted-foreground">Banned</p>
          </div>
        </div>

        {/* Pending Doctors Alert */}
        {pendingDoctors.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-yellow-400" />
                <div>
                  <p className="font-medium text-yellow-400">{pendingDoctors.length} Doctor(s) Pending Approval</p>
                  <p className="text-sm text-muted-foreground">Review and approve doctor registrations</p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('doctors')}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium"
              >
                Review
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'users' 
                ? 'bg-primary text-white' 
                : 'bg-card border border-border text-foreground hover:bg-muted'
            }`}
          >
            <Users className="w-4 h-4" />
            All Users
          </button>
          <button
            onClick={() => setActiveTab('doctors')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'doctors' 
                ? 'bg-cyan-500 text-white' 
                : 'bg-card border border-border text-foreground hover:bg-muted'
            }`}
          >
            <Stethoscope className="w-4 h-4" />
            Doctors
            {pendingDoctors.length > 0 && (
              <span className="px-2 py-0.5 bg-yellow-500 text-white rounded-full text-xs">
                {pendingDoctors.length}
              </span>
            )}
          </button>
        </div>

        {/* Doctors Tab Content */}
        {activeTab === 'doctors' && (
          <div className="space-y-4 mb-8">
            {pendingDoctors.length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Pending Approval
                </h3>
                <div className="space-y-3">
                  {pendingDoctors.map((doctor) => (
                    <div key={doctor.user_id} className="bg-card border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Stethoscope className="w-5 h-5 text-cyan-400" />
                            <span className="font-medium text-foreground">{doctor.full_name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{doctor.email}</p>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            <span className="px-2 py-1 bg-muted rounded">License: {doctor.medical_license || 'N/A'}</span>
                            <span className="px-2 py-1 bg-muted rounded capitalize">{doctor.specialization || 'N/A'}</span>
                            <span className="px-2 py-1 bg-muted rounded">{doctor.hospital_affiliation || 'N/A'}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Registered: {new Date(doctor.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDoctorApproval(doctor.user_id, true)}
                            disabled={approvingDoctor === doctor.user_id}
                            className="flex items-center gap-1 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleDoctorApproval(doctor.user_id, false)}
                            disabled={approvingDoctor === doctor.user_id}
                            className="flex items-center gap-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Approved Doctors List */}
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mt-6">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Approved Doctors
            </h3>
            <div className="space-y-2">
              {users.filter(u => u.user_type === 'doctor' && u.doctor_status === 'approved').map((doctor) => (
                <div key={doctor.user_id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-5 h-5 text-cyan-400" />
                      <span className="font-medium text-foreground">{doctor.full_name}</span>
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">Approved</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{doctor.email}</p>
                  </div>
                  <p className="text-xs text-muted-foreground capitalize">{doctor.specialization}</p>
                </div>
              ))}
              {users.filter(u => u.user_type === 'doctor' && u.doctor_status === 'approved').length === 0 && (
                <p className="text-center py-8 text-muted-foreground">No approved doctors yet</p>
              )}
            </div>
          </div>
        )}

        {/* User List - Only show when users tab is active */}
        {activeTab === 'users' && (loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-xl">
            <Users className="w-12 h-12 text-muted mx-auto mb-4" />
            <p className="text-muted-foreground">No users found</p>
          </div>
        ) : (
          <div>
            {users.map((u) => (
              <UserRow
                key={u.user_id}
                user={u}
                onUpdate={fetchUsers}
                onDelete={handleDeleteUser}
                onViewChats={handleViewChats}
              />
            ))}
          </div>
        ))}
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
};
